/**
 * REQ-002 MARKETING / FASE 1 (BHM-CDX-DIR-098) — estado puro de la superficie Marketing.
 * Mappers sin DOM para testabilidad. Sin datos ficticios: no existen métricas en esta fase;
 * la analítica externa queda NO_DATA hasta fuente real (fase posterior).
 */
import type { MarketingBrand } from '../stores/marketingApi';

export type MarketingListState = 'loading' | 'error' | 'empty' | 'ready';

export function marketingListState(input: {
  loading: boolean;
  failed: boolean;
  brands: MarketingBrand[] | null;
}): MarketingListState {
  if (input.loading || input.brands === null) return input.failed ? 'error' : 'loading';
  if (input.failed) return 'error';
  return input.brands.length === 0 ? 'empty' : 'ready';
}

/** marketing.write habilita acciones de escritura en la UI (el backend sigue siendo la autoridad). */
export function canWriteMarketing(permissions: readonly string[]): boolean {
  return permissions.includes('marketing.write');
}

/**
 * marketing.approve habilita acciones de aprobación (FASE 3 — DIR-100).
 * Es una capacidad DISTINTA de marketing.write: write NO implica approve.
 */
export function canApproveMarketing(permissions: readonly string[]): boolean {
  return permissions.includes('marketing.approve');
}

export const APPROVAL_DECISION_LABELS: Record<string, string> = {
  APPROVED: 'Aprobado',
  REJECTED: 'Rechazado',
};

/** Form del Brand Brain: cada lista se edita como una línea por elemento. */
export interface BrandBrainForm {
  description: string;
  targetAudience: string;
  keyMessages: string;
  avoid: string;
  hashtags: string;
  competitors: string;
}

export function linesToField(lines: readonly string[] | undefined): string {
  return (lines ?? []).join('\n');
}

export function fieldToLines(value: string): string[] {
  return value
    .split('\n')
    .map((line) => line.trim())
    .filter((line) => line.length > 0);
}

export function brandToForm(brand: MarketingBrand): BrandBrainForm {
  return {
    description: brand.description ?? '',
    targetAudience: brand.targetAudience ?? '',
    keyMessages: linesToField(brand.keyMessages),
    avoid: linesToField(brand.avoid),
    hashtags: linesToField(brand.hashtags),
    competitors: linesToField(brand.competitors),
  };
}

export function formToBrandBrain(form: BrandBrainForm): {
  description: string;
  targetAudience: string;
  keyMessages: string[];
  avoid: string[];
  hashtags: string[];
  competitors: string[];
} {
  return {
    description: form.description,
    targetAudience: form.targetAudience,
    keyMessages: fieldToLines(form.keyMessages),
    avoid: fieldToLines(form.avoid),
    hashtags: fieldToLines(form.hashtags),
    competitors: fieldToLines(form.competitors),
  };
}

export const BRAND_TONE_OPTIONS = [
  'professional',
  'casual',
  'playful',
  'inspirational',
  'educational',
] as const;

export const BRAND_TONE_LABELS: Record<string, string> = {
  professional: 'Profesional',
  casual: 'Casual',
  playful: 'Divertido',
  inspirational: 'Inspirador',
  educational: 'Educativo',
};

// ─── FASE 2 (BHM-CDX-DIR-099) — Posts ──────────────────────────────────────

/** Los 7 estados del contrato HubMarketingPostStatus, con label legible. */
export const POST_STATUS_LABELS: Record<string, string> = {
  draft: 'Borrador',
  pending_approval: 'Pendiente de aprobación',
  approved: 'Aprobado',
  rejected: 'Rechazado',
  scheduled: 'Programado',
  published: 'Publicado',
  failed: 'Fallido',
};

/**
 * Estados en los que este ciclo ofrece acciones reales de edición.
 * approved/rejected/scheduled/published/failed: el backend es la autoridad y las
 * decisiones de aprobación pertenecen al bloque 100 (APPROVALS) — sin acciones falsas.
 */
export function isPostEditable(status: string): boolean {
  return status === 'draft' || status === 'rejected';
}

/** Única transición real de este ciclo: draft → pending_approval. */
export function canSubmitPost(status: string): boolean {
  return status === 'draft';
}

export interface PostForm {
  content: string;
  hashtags: string;
}

export function postToForm(post: { content: string; hashtags: string[] }): PostForm {
  return { content: post.content ?? '', hashtags: linesToField(post.hashtags) };
}

export function formToPost(form: PostForm): { content: string; hashtags: string[] } {
  return { content: form.content, hashtags: fieldToLines(form.hashtags) };
}
